// ─────────────────────────────────────────────────────────────────────────
// currency — ONE place that knows how a stored money value becomes display
// text. Before this file the same three rules were re-implemented (slightly
// differently) in the finance panel, DetailViewV1's rate history, the detail
// field render engine and each host app's header config, so the same rate
// could read as "$100", "USD100" and "1.2e+06" on three surfaces of one record.
//
// Nothing here is module-, field- or customer-specific: callers pass the values
// their admin config resolved, these helpers only normalise them.
// ─────────────────────────────────────────────────────────────────────────

// ISO code → symbol. A value that is ALREADY a symbol passes through, so a
// record storing "$" and one storing "USD" render identically.
export const CURRENCY_SYMBOLS = {
  USD: '$', INR: '₹', GBP: '£', EUR: '€', CAD: 'C$', AUD: 'A$', SGD: 'S$', JPY: '¥',
};

// currencySymbol('USD') → '$' | currencySymbol('₹') → '₹' | currencySymbol('') → fallback
//
// The `/^[A-Za-z]{2,}$/` test is what separates "an unmapped ISO-ish CODE" from
// "an actual symbol": a 2+ letter run is a code we don't know (rendering it
// verbatim would print "AEDX100"), anything else is already printable.
export function currencySymbol(value, fallback = '$') {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const mapped = CURRENCY_SYMBOLS[raw.toUpperCase()];
  if (mapped) return mapped;
  return /^[A-Za-z]{2,}$/.test(raw) ? fallback : raw;
}

// Duration words, however they were stored, collapsed to one display label.
const RATE_UNITS = {
  hr: 'Hourly', hour: 'Hourly', hours: 'Hourly', hourly: 'Hourly',
  day: 'Daily', daily: 'Daily',
  week: 'Weekly', weekly: 'Weekly',
  month: 'Monthly', monthly: 'Monthly',
  year: 'Yearly', yearly: 'Yearly', annual: 'Yearly', annually: 'Yearly',
};

// rateUnitLabel('hr') → 'Hourly'. An unmapped unit keeps its own wording
// (capitalised) rather than being forced into a guess — a project that adds
// "Per Sprint" as a unit shows "Per Sprint", not "Hourly".
export function rateUnitLabel(value, fallback = '') {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  return RATE_UNITS[raw.toLowerCase()] ?? (raw.charAt(0).toUpperCase() + raw.slice(1));
}

// The short form used inline after an amount ("$100 /hr").
const RATE_UNIT_SHORT = {
  Hourly: 'hr', Daily: 'day', Weekly: 'wk', Monthly: 'mo', Yearly: 'yr',
};

export function rateUnitShort(value, fallback = '') {
  const label = rateUnitLabel(value, '');
  if (!label) return fallback;
  return RATE_UNIT_SHORT[label] ?? label.toLowerCase();
}

// toNumber — EXPONENT-SAFE parsing. This is the one that matters:
// Go's encoding/json marshals a large float64 as "1.2e+07", so the API really
// does return that string for a 12,000,000 client rate. The old approach
// (`Number(String(v).replace(/[^0-9.-]/g, ''))`) stripped the "e" and the "+"
// and silently produced 1.207 — a corrupted number that still looked plausible.
//
// So: strip only the characters that are definitely decoration (currency
// symbols, spaces, thousands separators) and let Number() handle the rest,
// because Number('1.2e+07') is exactly 12000000.
export function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const raw = String(value ?? '').trim().replace(/[,\s]/g, '').replace(/[^0-9eE+.\-]/g, '');
  if (!raw) return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

// formatAmount(12000000) → '12,000,000'   formatAmount('1.2e+07') → '12,000,000'
// Never returns exponent notation. A value that isn't numeric at all comes back
// untouched, so a free-text rate ("Negotiable") is not mangled into "NaN".
export function formatAmount(value, { decimals } = {}) {
  const n = toNumber(value);
  if (Number.isNaN(n)) return String(value ?? '');
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 2,
  });
}

// formatMoney(12000000, 'USD') → '$12,000,000'
// Negatives use a real unicode minus so amounts line up in tabular-nums columns.
export function formatMoney(value, currency = '$', options = {}) {
  const n = toNumber(value);
  if (Number.isNaN(n)) return String(value ?? '');
  const symbol = currencySymbol(currency);
  return `${n < 0 ? '−' : ''}${symbol}${formatAmount(Math.abs(n), options)}`;
}

// formatRate — amount + unit, with the separator emitted ONLY when there is a
// unit to separate. The trailing-slash bugs ("$12,000,000 /") all came from
// templates that appended the separator unconditionally.
export function formatRate(value, currency = '$', unit, options = {}) {
  const money = formatMoney(value, currency, options);
  const label = rateUnitLabel(unit, '');
  return label ? `${money} / ${label}` : money;
}
